import { ImageSourcePropType } from 'react-native';

import { VipTicketPng } from '../../../assets/bonuses';
import NameTagChangePacketPng from '../../../assets/storeModal/nametag-change-packet.png';
import i18n from '../../../i18n/i18n';
import { InventoryCategoryKey } from './InventoryCategories';

/**
 * The inventory has no backend: there is no store slice, no API and no owned-items feed anywhere
 * in the app. So the grid is placeholder-driven, the way `createPlaceholderEvent` feeds the event
 * dashboard and the Daily Missions modal feeds its mission list - the layout is real, the
 * contents are the design's.
 *
 * Figma "Inventory / Row 1" (4038:30492) draws the three states an item can be in: `Item / None`
 * (the empty slot that clears the equipped item), `Item / Classic / Equipped` (owned and worn) and
 * `Item / Royal` (not owned, priced).
 */
export type InventoryItemState = 'none' | 'equipped' | 'owned' | 'locked';

export type InventoryItem = {
	key: string;
	/** English name; the default for `nameKey`, which is translated where the item is rendered. */
	name: string;
	nameKey: string;
	state: InventoryItemState;
	/** Set on locked items - what the design prints beside the chip. */
	price?: number;
	/** Optional product-specific artwork; other inventory items use the default frame artwork. */
	artwork?: ImageSourcePropType;
	/** A stack the player holds rather than a single item: printed as "Amount: N" under the name. */
	amount?: number;
};

// The twelve the design draws, in its order: "Inventory / Row 1".."Row 4" (4038:30492..30561).
const frames: InventoryItem[] = [
	{ key: 'none', name: 'None', nameKey: 'T_SITE_INVENTORY_ITEM_NONE', state: 'none' },
	{ key: 'classic', name: 'Classic', nameKey: 'T_SITE_INVENTORY_ITEM_CLASSIC', state: 'equipped' },
	{
		key: 'royal',
		name: 'Royal',
		nameKey: 'T_SITE_INVENTORY_ITEM_ROYAL',
		state: 'locked',
		price: 5000,
	},
	{
		key: 'fire',
		name: 'Fire',
		nameKey: 'T_SITE_INVENTORY_ITEM_FIRE',
		state: 'locked',
		price: 10000,
	},
	{ key: 'ice', name: 'Ice', nameKey: 'T_SITE_INVENTORY_ITEM_ICE', state: 'locked', price: 7500 },
	{
		key: 'forest',
		name: 'Forest',
		nameKey: 'T_SITE_INVENTORY_ITEM_FOREST',
		state: 'locked',
		price: 12500,
	},
	{
		key: 'galaxy',
		name: 'Galaxy',
		nameKey: 'T_SITE_INVENTORY_ITEM_GALAXY',
		state: 'locked',
		price: 20000,
	},
	{
		key: 'dragon',
		name: 'Dragon',
		nameKey: 'T_SITE_INVENTORY_ITEM_DRAGON',
		state: 'locked',
		price: 25000,
	},
	{
		key: 'hearts',
		name: 'Hearts',
		nameKey: 'T_SITE_INVENTORY_ITEM_HEARTS',
		state: 'locked',
		price: 8000,
	},
	{
		key: 'shadow',
		name: 'Shadow',
		nameKey: 'T_SITE_INVENTORY_ITEM_SHADOW',
		state: 'locked',
		price: 15000,
	},
	{
		key: 'crystal',
		name: 'Crystal',
		nameKey: 'T_SITE_INVENTORY_ITEM_CRYSTAL',
		state: 'locked',
		price: 18000,
	},
	{
		key: 'sakura',
		name: 'Sakura',
		nameKey: 'T_SITE_INVENTORY_ITEM_SAKURA',
		state: 'locked',
		price: 30000,
	},
];

const backgrounds: InventoryItem[] = [
	{ key: 'none', name: 'None', nameKey: 'T_SITE_INVENTORY_ITEM_NONE', state: 'none' },
	{ key: 'felt', name: 'Felt', nameKey: 'T_SITE_INVENTORY_ITEM_FELT', state: 'equipped' },
	{
		key: 'velvet',
		name: 'Velvet',
		nameKey: 'T_SITE_INVENTORY_ITEM_VELVET',
		state: 'locked',
		price: 5000,
	},
	{
		key: 'midnight',
		name: 'Midnight',
		nameKey: 'T_SITE_INVENTORY_ITEM_MIDNIGHT',
		state: 'locked',
		price: 7500,
	},
	{
		key: 'sunset',
		name: 'Sunset',
		nameKey: 'T_SITE_INVENTORY_ITEM_SUNSET',
		state: 'locked',
		price: 10000,
	},
	{
		key: 'marble',
		name: 'Marble',
		nameKey: 'T_SITE_INVENTORY_ITEM_MARBLE',
		state: 'locked',
		price: 12500,
	},
];

const badges: InventoryItem[] = [
	{ key: 'none', name: 'None', nameKey: 'T_SITE_INVENTORY_ITEM_NONE', state: 'none' },
	{ key: 'rookie', name: 'Rookie', nameKey: 'T_SITE_INVENTORY_ITEM_ROOKIE', state: 'owned' },
	{ key: 'sharp', name: 'Sharp', nameKey: 'T_SITE_INVENTORY_ITEM_SHARP', state: 'equipped' },
	{
		key: 'highroller',
		name: 'High Roller',
		nameKey: 'T_SITE_INVENTORY_ITEM_HIGH_ROLLER',
		state: 'locked',
		price: 8000,
	},
	{
		key: 'champion',
		name: 'Champion',
		nameKey: 'T_SITE_INVENTORY_ITEM_CHAMPION',
		state: 'locked',
		price: 15000,
	},
	{
		key: 'legendary',
		name: 'Legendary',
		nameKey: 'T_SITE_INVENTORY_ITEM_LEGENDARY',
		state: 'locked',
		price: 40000,
	},
];

const featured: InventoryItem[] = [
	{
		key: 'svara-gold',
		name: 'Svara Gold',
		nameKey: 'T_SITE_INVENTORY_ITEM_SVARA_GOLD',
		state: 'locked',
		price: 25000,
	},
	{
		key: 'winter',
		name: 'Winter',
		nameKey: 'T_SITE_INVENTORY_ITEM_WINTER',
		state: 'locked',
		price: 18000,
	},
	{
		key: 'carnival',
		name: 'Carnival',
		nameKey: 'T_SITE_INVENTORY_ITEM_CARNIVAL',
		state: 'locked',
		price: 22000,
	},
	{
		key: 'name-changer',
		name: 'Name changer',
		nameKey: 'T_SITE_INVENTORY_ITEM_NAME_CHANGER',
		state: 'locked',
		price: 20000,
		artwork: NameTagChangePacketPng,
	},
];

/**
 * The one real item in the grid: the player's VIP Tickets, from the live wallet. Tickets are only
 * won as rewards, never bought, so the card carries the balance where other items show a price.
 */
export const vipTicketsItem = (balance: number): InventoryItem => ({
	key: 'vip-tickets',
	name: 'VIP Tickets',
	nameKey: 'T_SITE_INVENTORY_ITEM_VIP_TICKETS',
	state: 'owned',
	artwork: VipTicketPng,
	amount: balance,
});

export const INVENTORY_ITEMS: Record<InventoryCategoryKey, InventoryItem[]> = {
	frame: frames,
	background: backgrounds,
	badge: badges,
	featured,
	rewards: [],
};

/**
 * Getters, so each read goes through i18n at render time rather than at module load -
 * translations arrive from the backend after start-up.
 */
const intro = (titleKey: string, title: string, subtitleKey: string, subtitle: string) => ({
	get title() {
		return i18n.t(titleKey, { defaultValue: title });
	},
	get subtitle() {
		return i18n.t(subtitleKey, { defaultValue: subtitle });
	},
});

/** The heading and line the design prints above the grid, per category. */
export const INVENTORY_INTRO: Record<InventoryCategoryKey, { title: string; subtitle: string }> = {
	frame: intro(
		'T_SITE_INVENTORY_CATEGORY_PROFILE_FRAME',
		'Profile Frame',
		'T_SITE_INVENTORY_PROFILE_FRAME_HINT',
		'Choose and equip a profile frame.',
	),
	background: intro(
		'T_SITE_INVENTORY_CATEGORY_BACKGROUND',
		'Background',
		'T_SITE_INVENTORY_BACKGROUND_HINT',
		'Choose and equip a profile background.',
	),
	badge: intro(
		'T_SITE_INVENTORY_CATEGORY_BADGE',
		'Badge',
		'T_SITE_INVENTORY_BADGE_HINT',
		'Choose and equip a badge.',
	),
	featured: intro(
		'T_SITE_INVENTORY_CATEGORY_FEATURED',
		'Featured',
		'T_SITE_INVENTORY_FEATURED_HINT',
		'Limited items, available for a while.',
	),
	rewards: intro(
		'T_SITE_DUELS_REWARDS',
		'Rewards',
		'T_SITE_INVENTORY_REWARDS_HINT',
		'Season rewards you’ve unlocked in Duels.',
	),
};
